import { useCallback, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CancelSubscriptionConfirmModal from './components/CancelSubscriptionConfirmModal';
import CancelSubscriptionProcessingModal from './components/CancelSubscriptionProcessingModal';
import CancelSubscriptionSuccessModal from './components/CancelSubscriptionSuccessModal';
import type { CancelSubscriptionBillingPreview, CancelSubscriptionPayload } from './types';
import { formatDollars } from '@/utils/helperFunctions';
import { queryClient } from '@/providers/AllProviders';

export { buildCancelSubscriptionBillingPreview } from './buildBillingPreview';
export type { CancelSubscriptionBillingPreview, CancelSubscriptionPayload, CancelSubscriptionResponse } from './types';

type FlowStep = 'idle' | 'confirm' | 'processing' | 'success';

export type CancelSubscriptionProps = {
  billingPreview: CancelSubscriptionBillingPreview | null;
  /** When false, nothing is rendered */
  enabled?: boolean;
  cancelSubscription: any;
  agencyView?: boolean;
};

export default function CancelSubscription({
  billingPreview,
  enabled = true,
  cancelSubscription,
  agencyView = false
}: CancelSubscriptionProps) {
  const navigate = useNavigate();
  const [flow, setFlow] = useState<FlowStep>('idle');
  const [finalChargeFormatted, setFinalChargeFormatted] = useState('');

  const { mutate, reset, isPending } = cancelSubscription;

  const handleSuccessDone = useCallback(() => {
    setFlow('idle');
    reset();
    navigate('/', { replace: true });
    queryClient.invalidateQueries({ queryKey: ['businessProfile'] });
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
    queryClient.invalidateQueries({ queryKey: ['agency_organizations'] });
  }, [navigate, reset]);

  const closeFlow = useCallback(() => {
    setFlow('idle');
    reset();
  }, [reset]);

  const handleConfirm = (payload: CancelSubscriptionPayload) => {
    if (!billingPreview) return;
    setFlow('processing');

    mutate(payload, {
      onSuccess: (data: any) => {
        const charged = data?.charged_amount;
        setFinalChargeFormatted(
          charged !== undefined && charged !== null ? formatDollars(charged) : billingPreview.totalFormatted
        );
        setFlow('success');
      },
      onError: () => {
        setFlow('confirm');
      }
    });
  };

  if (!enabled || !billingPreview) return null;

  return (
    <>
      <Box
        sx={{
          mt: { xs: 2, lg: 3 },
          p: { xs: 2, lg: 2.5 },
          borderRadius: 2,
          bgcolor: '#FEF2F2',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box flex={1} minWidth={0}>
          <Typography fontWeight={500} color='error.main' fontSize={{ xs: '1rem', sm: '1.05rem' }} mb={0.5}>
            Cancel Subscription
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: 13, sm: 14 }, lineHeight: 1.5 }}>
            Cancelling will calculate your bill to date, charge the pro-rated amount, and remove all payment methods
          </Typography>
        </Box>
        <Button
          variant='contained'
          onClick={() => setFlow('confirm')}
          sx={{
            bgcolor: 'error.main',
            fontWeight: 500,
            px: 3,
            py: 1.25,
            borderRadius: 999,
            whiteSpace: 'nowrap',
            alignSelf: { xs: 'stretch', sm: 'center' },
            '&:hover': { bgcolor: '#B91C1C' }
          }}
        >
          Cancel Subscription
        </Button>
      </Box>

      <CancelSubscriptionConfirmModal
        open={flow === 'confirm'}
        isAgencyView={agencyView}
        preview={billingPreview}
        onClose={closeFlow}
        onConfirm={handleConfirm}
      />

      <CancelSubscriptionProcessingModal
        open={flow === 'processing'}
        onClose={() => setFlow('confirm')}
        disableClose={isPending}
      />

      <CancelSubscriptionSuccessModal
        open={flow === 'success'}
        finalChargeFormatted={finalChargeFormatted}
        onDone={handleSuccessDone}
      />
    </>
  );
}
